import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Send, Plus, Mail, MessageSquare } from "lucide-react";

export default function FollowUps() {
  const [list, setList] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ estimate_id: "", channel: "email", scheduled_at: "", message: "" });

  const load = () => api.get("/followups").then((r) => setList(r.data));
  useEffect(() => {
    load();
    api.get("/estimates").then((r) => setEstimates(r.data));
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const iso = new Date(form.scheduled_at).toISOString();
      await api.post("/followups", { ...form, scheduled_at: iso });
      toast.success("Follow-up scheduled");
      setForm({ estimate_id: "", channel: "email", scheduled_at: "", message: "" });
      setOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const sendNow = async (id) => {
    try {
      const { data } = await api.post(`/followups/${id}/send`);
      if (data.detail?.status === "mocked") toast.info("SENT IN MOCK MODE — configure Resend/Twilio in Settings for real delivery");
      else toast.success("Sent!");
      load();
    } catch (e) { toast.error("Send failed"); }
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-primary mb-1">Automation</p>
          <h1 className="font-display text-4xl font-black tracking-tight">Follow-ups</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-followup-btn" className="h-11 rounded-sm btn-kinetic"><Plus className="w-4 h-4 mr-1" /> Schedule</Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader><DialogTitle className="font-display text-2xl">New follow-up</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <div>
                <Label>Estimate</Label>
                <Select value={form.estimate_id} onValueChange={(v) => setForm({ ...form, estimate_id: v })}>
                  <SelectTrigger data-testid="followup-estimate-select" className="h-11 rounded-sm"><SelectValue placeholder="Select an estimate" /></SelectTrigger>
                  <SelectContent>
                    {estimates.map((e) => <SelectItem key={e.id} value={e.id}>{e.niche} — ${e.total?.toFixed(0)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Channel</Label>
                <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                  <SelectTrigger data-testid="followup-channel-select" className="h-11 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Send at</Label>
                <Input data-testid="followup-datetime" type="datetime-local" required value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="h-11 rounded-sm" />
              </div>
              <div>
                <Label>Message</Label>
                <Textarea data-testid="followup-message" rows={3} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-sm" placeholder="Hi Kevin — just checking in on the quote we sent you..." />
              </div>
              <Button type="submit" data-testid="followup-save-btn" className="w-full h-11 rounded-sm">Schedule</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {list.length === 0 ? (
        <div className="bento-card p-12 text-center">
          <Send className="w-10 h-10 text-primary mx-auto mb-3" />
          <p className="text-muted-foreground">No follow-ups yet. Automate reminders to convert more quotes.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((f) => (
            <div key={f.id} data-testid={`followup-${f.id}`} className="bento-card p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {f.channel === "email" ? <Mail className="w-5 h-5 text-primary" /> : <MessageSquare className="w-5 h-5 text-primary" />}
                <div>
                  <p className="font-medium text-sm">{f.message.slice(0, 80)}{f.message.length > 80 ? "…" : ""}</p>
                  <p className="text-xs text-muted-foreground">Scheduled {new Date(f.scheduled_at).toLocaleString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={f.status === "sent" ? "bg-emerald-100 text-emerald-900" : f.status === "failed" ? "bg-red-100 text-red-900" : "bg-slate-200 text-slate-900"}>{f.status}</Badge>
                {f.status === "scheduled" && <Button data-testid={`send-now-${f.id}`} size="sm" variant="outline" className="rounded-sm" onClick={() => sendNow(f.id)}>Send now</Button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
